<script lang="ts" setup>
// Figma name: Badge
// https://www.figma.com/file/1HfA941cU4A9RZxXHLmbpG/V7-Design-System-(WIP)?type=design&node-id=682-26397&mode=dev

/**
 * This is still WIP and requires implementation of remaining variants as well as potentially hover effects.
 */

import { twMerge } from 'tailwind-merge'
import { computed } from 'vue'
import type { IconName } from './IconName'
import IconSprite from './IconSprite.vue'

const props = defineProps<{
  label: string
  leadingIcon?: IconName | undefined
  trailingIcon?: IconName
  trailingIconAriaLabel?: string
  variant: 'blue' | 'warning' | 'neutral' | 'transparent' | 'selected' | 'critical'
  size: 'xs' | 'sm' | 'md'
  rainbowColor?: string | null
  underline?: boolean
  readonly?: boolean
  /**
   * When true, the badge will be a <button> element - will be able to receive keyboard
   * focus and will be styled to indicate that it is interactive.
   */
  interactive?: boolean
}>()

defineEmits<{
  (e: 'trailingIconClick', event: Event): void
}>()

defineSlots<{
  'leading-icon': undefined
  'trailing-icon': undefined
}>()

const tag = computed(() => (props.interactive ? 'button' : 'div'))
</script>

<template>
  <component
    :is="tag"
    :class="[
      twMerge('shrink-0 truncate', String($attrs.class || '')),
      size === 'xs' && ['rounded-corner-4'],
      size === 'sm' && ['rounded-corner-6'],
      size === 'md' && ['rounded-corner-8'],
      variant === 'blue' && ['bg-background-blue-subtle text-icon-primary'],
      variant === 'warning' && ['bg-background-warning-subtle text-text-warning'],
      variant === 'neutral' && ['bg-background-gray-subtlest text-text-subtle'],
      variant === 'transparent' && ['bg-background-transparent text-text-subtle'],
      variant === 'selected' && ['bg-background-selected text-text-selected'],
      variant === 'critical' && ['bg-background-critical-subtle text-text-critical'],
      interactive && 'cursor-pointer outline-none focus-visible:shadow-focus-ring-primary',
    ]"
    :style="
      rainbowColor
        ? {
            backgroundColor: `var(--color-background-${rainbowColor}-subtle)`,
            color: `var(--color-text-${rainbowColor})`,
          }
        : undefined
    "
    :title="label"
  >
    <div
      class="flex shrink-0 items-center"
      :class="[
        size === 'xs' && ['rounded-corner-4 px-0.5 text-xs-11px-default'],
        size === 'sm' && ['rounded-corner-6 p-0.5 text-sm-12px-default'],
        size === 'md' && ['rounded-corner-8 p-0.5 text-md-13px-default'],
      ]"
    >
      <IconSprite
        v-if="leadingIcon"
        class="pl-0.5"
        :icon="leadingIcon"
        data-test="leading-icon"
        :size="size === 'md' ? 'lg' : 'xs'"
      />
      <slot
        v-else
        name="leading-icon"
        class="pl-0.5"
      />

      <span
        class="truncate"
        :class="[
          size === 'xs' && 'px-0.5',
          size === 'sm' && 'px-1',
          size === 'md' && 'px-1',
          underline && 'hover:underline',
          readonly && 'text-text-disabled',
        ]"
        >{{ label }}</span
      >

      <slot name="trailing-icon">
        <button
          v-if="trailingIcon"
          class="cursor-pointer rounded-corner-4 hover:bg-background-transparent-hovered focus:outline-hidden focus-visible:bg-background-transparent-pressed"
          :class="[size === 'xs' && 'p-0.5', size === 'sm' && 'p-0.5', size === 'md' && 'p-0.5']"
          :aria-label="trailingIconAriaLabel"
          @click="$emit('trailingIconClick', $event)"
        >
          <IconSprite
            data-test="trailing-icon"
            :size="size === 'md' ? 'lg' : 'xs'"
            :icon="trailingIcon"
          />
        </button>
      </slot>
    </div>
  </component>
</template>
